// ============================================================================
// OverlappingPoints ViewModel
// ============================================================================

import { useState, useCallback, useMemo } from "react";
import type {
	OverlappingPointsProps,
	OverlappingPointsViewModel,
	MapFeatureData,
	DeleteConfirmation,
	BulkDeleteOption,
} from "./OverlappingPoints.types";
import type { DuplicateGroup } from "../../WizardContainer";
import { DUPLICATE_GROUP_COLORS, DUPLICATE_GROUP_COLOR_NAMES } from "../../shared/constants";

// Point sizes for stacking (largest at bottom)
const POINT_SIZES = [24, 18, 12, 8, 6];

/**
 * ViewModel hook for OverlappingPoints component
 */
export function useOverlappingPointsViewModel(props: OverlappingPointsProps): OverlappingPointsViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onDeleteFeature, onDeleteFeatures } = props;

	// Track which features are hidden on the map
	const [hiddenFeatureIds, setHiddenFeatureIds] = useState<Set<string>>(new Set());
	// State for delete confirmation dialog (single or bulk)
	const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;

	// Create unique feature ID
	const createFeatureId = useCallback((groupId: string, featureIndex: number): string => {
		return `${groupId}-${featureIndex}`;
	}, []);

	// Toggle feature visibility
	const toggleFeatureVisibility = useCallback((featureId: string) => {
		setHiddenFeatureIds((prev) => {
			const next = new Set(prev);
			if (next.has(featureId)) {
				next.delete(featureId);
			} else {
				next.add(featureId);
			}
			return next;
		});
	}, []);

	// Show all features
	const showAllFeatures = useCallback(() => {
		setHiddenFeatureIds(new Set());
	}, []);

	// Hide all features
	const hideAllFeatures = useCallback(() => {
		if (!currentIssue) return;
		const allIds = new Set<string>();
		for (const group of currentIssue.duplicateGroups) {
			for (let i = 0; i < group.features.length; i++) {
				allIds.add(createFeatureId(group.id, group.featureIndices[i]));
			}
		}
		setHiddenFeatureIds(allIds);
	}, [currentIssue, createFeatureId]);

	// Get display name for a feature
	const getFeatureDisplayName = useCallback((group: DuplicateGroup, index: number): string => {
		const feature = group.features[index];
		const featureIndex = group.featureIndices[index];

		// Try to get a name from properties
		const props = feature.properties;
		if (props) {
			// Common name fields
			for (const field of ["name", "Name", "NAME", "id", "Id", "ID", "SAMPLE_ID", "SampleID"]) {
				if (props[field]) {
					return String(props[field]);
				}
			}
		}

		// Fallback to type + index
		return `Point #${featureIndex + 1}`;
	}, []);

	// Get total duplicate count
	const getTotalDuplicateCount = useCallback((): number => {
		if (!currentIssue) return 0;
		return currentIssue.duplicateGroups.reduce((sum, group) => sum + group.features.length, 0);
	}, [currentIssue]);

	// Get color for a position within group (same position = same color across all groups)
	const getPositionColor = useCallback((positionIndex: number): string => {
		return DUPLICATE_GROUP_COLORS[positionIndex % DUPLICATE_GROUP_COLORS.length];
	}, []);

	// Generate map features data
	const mapFeatures = useMemo((): MapFeatureData[] => {
		if (!currentIssue) return [];

		const features: MapFeatureData[] = [];

		currentIssue.duplicateGroups.forEach((group) => {
			group.features.forEach((feature, i) => {
				const featureIndex = group.featureIndices[i];
				// Color by position within group (not by group) so users can identify
				// which features to delete across all groups (e.g., "delete all the blue ones")
				const color = getPositionColor(i);
				features.push({
					feature,
					groupId: group.id,
					featureIndex,
					isDuplicate: true,
					color,
					size: POINT_SIZES[Math.min(i, POINT_SIZES.length - 1)],
				});
			});
		});

		return features;
	}, [currentIssue, getPositionColor]);

	// ── Bulk delete by position/color ──────────────────────────────────────
	const bulkDeleteByPosition = useMemo((): BulkDeleteOption[] => {
		if (!currentIssue) return [];

		// Find max position count across all groups
		const maxPositions = Math.max(...currentIssue.duplicateGroups.map((g) => g.features.length), 0);
		if (maxPositions < 2) return [];

		const options: BulkDeleteOption[] = [];
		for (let pos = 0; pos < maxPositions; pos++) {
			const pointIndices: number[] = [];

			for (const group of currentIssue.duplicateGroups) {
				if (pos < group.features.length) {
					const featureIndex = group.featureIndices[pos];
					pointIndices.push(featureIndex);
				}
			}

			if (pointIndices.length === 0) continue;

			const colorName = DUPLICATE_GROUP_COLOR_NAMES[pos % DUPLICATE_GROUP_COLOR_NAMES.length];
			const color = getPositionColor(pos);

			options.push({
				key: `pos-${pos}`,
				label: colorName,
				color,
				pointIndices,
				zoneIndices: [],
				count: pointIndices.length,
			});
		}

		return options;
	}, [currentIssue, getPositionColor]);

	// ── Bulk delete by file ────────────────────────────────────────────────
	const bulkDeleteByFile = useMemo((): BulkDeleteOption[] => {
		if (!currentIssue) return [];

		// Group feature indices by FilePath
		const fileMap = new Map<string, { pointIndices: number[] }>();

		for (const group of currentIssue.duplicateGroups) {
			for (let i = 0; i < group.features.length; i++) {
				const filePath = group.features[i].properties?.FilePath;
				if (!filePath) continue;

				if (!fileMap.has(filePath)) {
					fileMap.set(filePath, { pointIndices: [] });
				}
				const entry = fileMap.get(filePath)!;
				const featureIndex = group.featureIndices[i];
				entry.pointIndices.push(featureIndex);
			}
		}

		// Only show when 2+ unique files exist
		if (fileMap.size < 2) return [];

		const options: BulkDeleteOption[] = [];
		for (const [filePath, { pointIndices }] of fileMap) {
			const fileName = filePath.split("/").pop() || filePath;
			options.push({
				key: `file-${filePath}`,
				label: fileName,
				pointIndices,
				zoneIndices: [],
				count: pointIndices.length,
			});
		}

		return options;
	}, [currentIssue]);

	// ── Delete handlers ────────────────────────────────────────────────────

	// Handle single-feature delete with confirmation
	const handleDeleteFeatureClick = useCallback(
		(groupId: string, featureIndex: number, featureType: "point" | "zone") => {
			setDeleteConfirmation({ kind: "single", groupId, featureIndex, featureType });
		},
		[],
	);

	// Handle bulk delete chip click
	const handleBulkDeleteClick = useCallback((option: BulkDeleteOption) => {
		setDeleteConfirmation({ kind: "bulk", option });
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (!deleteConfirmation || !currentIssue) return;

		if (deleteConfirmation.kind === "single") {
			onDeleteFeature(currentIssue.job.id, deleteConfirmation.featureType, deleteConfirmation.featureIndex);
		} else {
			const { pointIndices, zoneIndices } = deleteConfirmation.option;
			onDeleteFeatures(currentIssue.job.id, pointIndices, zoneIndices);
		}

		setDeleteConfirmation(null);
	}, [deleteConfirmation, currentIssue, onDeleteFeature, onDeleteFeatures]);

	const handleCancelDelete = useCallback(() => {
		setDeleteConfirmation(null);
	}, []);

	// ── Dialog text computed from confirmation state ────────────────────────
	const deleteDialogTitle = useMemo(() => {
		if (!deleteConfirmation) return "";
		if (deleteConfirmation.kind === "single") return "Delete Feature";
		return `Delete ${deleteConfirmation.option.count} Features`;
	}, [deleteConfirmation]);

	const deleteDialogMessage = useMemo(() => {
		if (!deleteConfirmation) return "";
		if (deleteConfirmation.kind === "single") {
			return "Are you sure you want to delete this feature? This action cannot be undone.";
		}
		const { label, count } = deleteConfirmation.option;
		return `Are you sure you want to delete all ${count} "${label}" feature${count !== 1 ? "s" : ""} across all groups? This action cannot be undone.`;
	}, [deleteConfirmation]);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		hiddenFeatureIds,
		toggleFeatureVisibility,
		showAllFeatures,
		hideAllFeatures,
		deleteConfirmation,
		handleDeleteFeatureClick,
		handleBulkDeleteClick,
		handleConfirmDelete,
		handleCancelDelete,
		deleteDialogTitle,
		deleteDialogMessage,
		bulkDeleteByPosition,
		bulkDeleteByFile,
		getFeatureDisplayName,
		getTotalDuplicateCount,
		mapFeatures,
		getPositionColor,
	};
}
